import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Select, Textarea, TextInput } from "flowbite-react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { useAppSelector } from "../../Reducers/store";
import { UserLevelNames } from "../../Models/UserLevel";
import MemberEngagementApi, {
  MemberEngagementRow,
  MemberOutreachNotes,
} from "../../Api/MemberEngagementApi";
import Loader from "../Common/Loader";

const dayOptions = [90, 180, 365];

function formatWhen(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-US", {
    timeZone: "America/New_York",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function formatNoteWhen(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("en-US", {
    timeZone: "America/New_York",
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function statusLabel(status: string): string {
  if (status === "GoneCold") return "Gone cold";
  if (status === "NeverEngaged") return "Never engaged";
  return "Current";
}

const AdminMemberEngagementPage: React.FC = () => {
  const navigate = useNavigate();
  const { userModel } = useAppSelector((state) => state.user);
  const [portalDays, setPortalDays] = useState(180);
  const [eventDays, setEventDays] = useState(365);
  const [status, setStatus] = useState("cold");
  const [search, setSearch] = useState("");
  const [rows, setRows] = useState<MemberEngagementRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<MemberEngagementRow | null>(null);
  const [notes, setNotes] = useState<MemberOutreachNotes | null>(null);
  const [notesLoading, setNotesLoading] = useState(false);
  const [noteType, setNoteType] = useState("Call");
  const [noteText, setNoteText] = useState("");
  const [noteUserId, setNoteUserId] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (userModel && userModel.userLevelId !== UserLevelNames.SuperAdmin) {
      toast.error("You do not have permission to access this page.");
      navigate("/");
    }
  }, [userModel, navigate]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await MemberEngagementApi.getList(portalDays, eventDays, status);
      setRows(res.data ?? []);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load member engagement.");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [portalDays, eventDays, status]);

  useEffect(() => {
    if (userModel?.userLevelId === UserLevelNames.SuperAdmin) {
      load();
    }
  }, [userModel, load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      `${r.name ?? ""} ${r.subscriptionName ?? ""} ${r.phone ?? ""} ${r.lastEventName ?? ""}`
        .toLowerCase()
        .includes(q)
    );
  }, [rows, search]);

  const openMember = async (row: MemberEngagementRow) => {
    setSelected(row);
    setNoteText("");
    setNoteType("Call");
    setNoteUserId("");
    setNotes(null);
    try {
      setNotesLoading(true);
      const res = await MemberEngagementApi.getNotes(row.memberId);
      setNotes(res.data);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load notes.");
    } finally {
      setNotesLoading(false);
    }
  };

  const saveNote = async () => {
    if (!selected) return;
    if (!noteText.trim()) {
      toast.error("Write a note before saving.");
      return;
    }
    try {
      setSaving(true);
      await MemberEngagementApi.addNote(
        selected.memberId,
        noteType,
        noteText.trim(),
        noteUserId ? Number(noteUserId) : null
      );
      setNoteText("");
      const res = await MemberEngagementApi.getNotes(selected.memberId);
      setNotes(res.data);
      setRows((current) =>
        current.map((r) =>
          r.memberId === selected.memberId
            ? { ...r, latestNoteUtc: new Date().toISOString() }
            : r
        )
      );
      toast.success("Note saved.");
    } catch (err) {
      console.error(err);
      toast.error("Failed to save note.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="w-full p-4">
      <div className="mb-4">
        <h1 className="text-gray-900 font-bold text-3xl">Member Engagement</h1>
        <p className="text-sm text-gray-600 mt-1">
          Current members who have not used the portal or registered for an event
          in the selected windows. Gone cold means they were active before and
          have since gone quiet.
        </p>
      </div>

      <div className="flex flex-wrap gap-3 mb-4 items-end">
        <div>
          <label className="block text-xs text-gray-600 mb-1">Show</label>
          <Select value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="cold">Gone cold</option>
            <option value="never">Never engaged</option>
            <option value="quiet">All quiet</option>
          </Select>
        </div>
        <div>
          <label className="block text-xs text-gray-600 mb-1">Portal quiet after</label>
          <Select
            value={portalDays}
            onChange={(e) => setPortalDays(Number(e.target.value))}
          >
            {dayOptions.map((d) => (
              <option key={d} value={d}>
                {d} days
              </option>
            ))}
          </Select>
        </div>
        <div>
          <label className="block text-xs text-gray-600 mb-1">Event quiet after</label>
          <Select
            value={eventDays}
            onChange={(e) => setEventDays(Number(e.target.value))}
          >
            {dayOptions.map((d) => (
              <option key={d} value={d}>
                {d} days
              </option>
            ))}
          </Select>
        </div>
        <div className="flex-1 min-w-[200px]">
          <label className="block text-xs text-gray-600 mb-1">Search</label>
          <TextInput
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Member, phone, or event"
          />
        </div>
      </div>

      {loading && <Loader />}

      <div className="flex gap-4 items-start">
        <div className="flex-1 overflow-x-auto border rounded bg-white">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left">
              <tr>
                <th className="p-2">Member</th>
                <th className="p-2">Level</th>
                <th className="p-2">Phone</th>
                <th className="p-2">Last login</th>
                <th className="p-2">Last event</th>
                <th className="p-2">Users</th>
                <th className="p-2">Status</th>
                <th className="p-2">Last note</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && !loading ? (
                <tr>
                  <td className="p-3 text-gray-500" colSpan={8}>
                    No members match these filters.
                  </td>
                </tr>
              ) : (
                filtered.map((row) => (
                  <tr
                    key={row.memberId}
                    className={`border-t cursor-pointer hover:bg-gray-50 ${
                      selected?.memberId === row.memberId ? "bg-blue-50" : ""
                    }`}
                    onClick={() => openMember(row)}
                  >
                    <td className="p-2 font-medium">{row.name}</td>
                    <td className="p-2">{row.subscriptionName}</td>
                    <td className="p-2">{row.phone || "—"}</td>
                    <td className="p-2">{formatWhen(row.lastLoginDate)}</td>
                    <td className="p-2">
                      {row.lastEventDate ? (
                        <>
                          {formatWhen(row.lastEventDate)}
                          <div className="text-xs text-gray-500">
                            {row.lastEventName}
                            {row.lastEventAttended ? " · attended" : ""}
                          </div>
                        </>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="p-2">{row.userCount}</td>
                    <td className="p-2">{statusLabel(row.status)}</td>
                    <td className="p-2">{formatWhen(row.latestNoteUtc)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {selected && (
          <div className="w-full max-w-md border rounded bg-white p-4">
            <div className="flex justify-between items-start mb-3">
              <div>
                <h2 className="font-semibold text-lg">{selected.name}</h2>
                <p className="text-xs text-gray-500">Notes and calls</p>
              </div>
              <button
                type="button"
                className="text-sm text-gray-500"
                onClick={() => setSelected(null)}
              >
                Close
              </button>
            </div>

            {notesLoading && <Loader />}

            <div className="space-y-2 mb-4 max-h-80 overflow-y-auto">
              {(notes?.notes ?? []).length === 0 && !notesLoading ? (
                <p className="text-sm text-gray-500">No notes yet.</p>
              ) : (
                notes?.notes.map((note) => (
                  <div key={note.id} className="border rounded p-2 text-sm">
                    <div className="text-xs text-gray-500 mb-1">
                      {note.noteType}
                      {note.userName ? ` · ${note.userName}` : ""}
                      {" · "}
                      {formatNoteWhen(note.createdUtc)}
                      {note.authorName ? ` · ${note.authorName}` : ""}
                    </div>
                    <div className="whitespace-pre-wrap">{note.note}</div>
                  </div>
                ))
              )}
            </div>

            <div className="space-y-2">
              <Select value={noteType} onChange={(e) => setNoteType(e.target.value)}>
                <option value="Call">Call</option>
                <option value="Email">Email</option>
                <option value="Meeting">Meeting</option>
                <option value="Other">Other</option>
              </Select>
              <Select
                value={noteUserId}
                onChange={(e) => setNoteUserId(e.target.value)}
              >
                <option value="">About the company</option>
                {(notes?.users ?? []).map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.name}
                  </option>
                ))}
              </Select>
              <Textarea
                rows={4}
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                placeholder="What did you talk about?"
              />
              <Button disabled={saving} onClick={saveNote}>
                Save note
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminMemberEngagementPage;
